/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { fromEvent, debounceTime, distinctUntilChanged, tap } from 'rxjs'
import { filter } from 'rxjs/operators'
import { ListagensDiversasService } from '../../../services/listagens-diversas.service'
import { ActivatedRoute } from '@angular/router'
import { DatePipe } from '@angular/common'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { ConcursoService } from 'src/app/services/concurso.service'
import { ListaPresencaInscricaoDTO } from 'src/app/models/lista-presenca-inscricao'
import { SearchInputComponent, DefaultPaginatorComponent } from 'src/app/modules/shared'

@Component({
  selector: 'app-lista-presenca',
  templateUrl: './lista-presenca.component.html',
  styleUrls: ['./lista-presenca.component.scss'],
})
export class ListaPresencaComponent implements AfterViewInit, OnInit {
  @ViewChild('paginator', { static: true }) paginator!: DefaultPaginatorComponent
  @ViewChild(MatSort) sort!: MatSort
  @ViewChild('searchInput') searchInput!: SearchInputComponent

  totalElements: number = 0

  colunasGrid: string[] = ['nomeCandidato', 'cpf', 'status', 'classificacao', 'motivoClassificacao', 'presente', 'dataRegistro', 'usuarioRegistro', 'salvar']
  dataSource: MatTableDataSource<ListaPresencaInscricaoDTO> = new MatTableDataSource()

  idConcurso?: number
  concursoTitulo?: string
  provaAndamento?: string
  filtro: string = ''

  datepipe: DatePipe = new DatePipe('pt-BR')

  constructor(
    private listagensDiversasService: ListagensDiversasService,
    private route: ActivatedRoute,
    private unespCoreMessageService: UnespCoreMessageService,
    private readonly concursoService: ConcursoService
  ) {}

  ngOnInit(): void {
    ;(this.route.parent?.params || this.route.params).subscribe(params => {
      this.idConcurso = params['id']
      this.getData()
    })
  }

  ngAfterViewInit() {
    fromEvent<KeyboardEvent>(this.searchInput.inputRef.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        filter((e: KeyboardEvent) => e.keyCode === 13),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0
          this.getData()
        })
      )
      .subscribe()

    this.paginator?.page.pipe(tap(() => this.getData())).subscribe()
  }



  salvar(row: ListaPresencaInscricaoDTO) {
    if (row.presente !== null && row.presente !== undefined) {
      this.listagensDiversasService
        .atualizarPresencaInscricao(this.idConcurso, row.idInscricao, row.presente)
        .subscribe({
          next: (response) => {
            const mensagem = typeof response === 'string' ? response : `Presença de ${row.nomeCandidato} salva com sucesso`
            this.unespCoreMessageService.showMessageSuccess(mensagem)
            this.getData()
          },
          error: (err) => {
              this.unespCoreMessageService.showMessageError(
              err?.error?.message ?? 'Erro ao carregar a lista de concursos.'
            );    
          }
        })
    }
  }

  private getData() {
    this.listagensDiversasService
      .getListaPresenca(this.idConcurso!)
      .subscribe(data => {
        const inscricoes = data as ListaPresencaInscricaoDTO[]
        this.dataSource = new MatTableDataSource(inscricoes)
        this.dataSource.sort = this.sort
        this.totalElements = inscricoes.length
        
        // Pega a prova em andamento do primeiro registro
        if (inscricoes.length > 0) {
          this.provaAndamento = inscricoes[0].provaAndamento
        }
      })
      
    this.concursoService.getTitulo(this.idConcurso).subscribe(resp => {
      this.concursoTitulo = resp.concursoTitulo
    })
  }  

  selecionar(): void {
    this.getData()
  }

  isClassificacaoFinalizada(row: ListaPresencaInscricaoDTO): boolean {
    return row.classificacao === 'Habilitado' || row.classificacao === 'Não habilitado'
  }

  getPresencaIcon(presente: boolean | null): string {
    if (presente === true) return 'check'
    if (presente === false) return 'close'
    return 'remove'
  }

  getPresencaText(presente: boolean | null): string {
    if (presente === true) return 'Presente'
    if (presente === false) return 'Ausente'
    return 'Não definido'
  }
}